import * as React from "react";

/**
 * ButtonLink adapter interface for platform-specific navigation components.
 *
 * This adapter allows ButtonLink to work across different platforms:
 * - Web: React Router's Link component
 * - React Native: TouchableOpacity + React Navigation
 * - tvOS/Roku: Platform-specific focusable navigation components
 * - Desktop: Same as web (Electron uses React Router)
 */
export type ButtonLinkAdapter = (props: {
  children: React.ReactNode;
  href: string;
  disabled?: boolean;
  variant?: "primary" | "ghost" | "compact" | "ghost-sm" | "overlay" | "outline";
  className?: string;
}) => React.ReactElement;

let impl: ButtonLinkAdapter | null = null;

/**
 * Register a platform-specific ButtonLink adapter.
 *
 * @param adapter - The adapter implementation for the current platform
 *
 * @example
 * // Web adapter (React Router)
 * registerButtonLink(({ children, href, variant, disabled, className }) => (
 *   <Link
 *     to={href}
 *     className={getButtonClasses(variant, disabled, className)}
 *     aria-disabled={disabled}
 *     onClick={(e) => disabled && e.preventDefault()}
 *   >
 *     {children}
 *   </Link>
 * ));
 *
 * @example
 * // React Native adapter
 * registerButtonLink(({ children, href, variant, disabled }) => {
 *   const navigation = useNavigation();
 *   return (
 *     <TouchableOpacity
 *       onPress={() => navigation.navigate(href)}
 *       disabled={disabled}
 *       style={getButtonStyles(variant, disabled)}
 *     >
 *       <Text>{children}</Text>
 *     </TouchableOpacity>
 *   );
 * });
 */
export const registerButtonLink = (adapter: ButtonLinkAdapter) => {
  impl = adapter;
};

/**
 * ButtonLink component - Navigation links styled as buttons.
 *
 * Uses the headless + adapter pattern to support cross-platform navigation
 * while maintaining consistent button styling.
 *
 * @param children - Content to display inside the button link
 * @param to - Navigation target (route path, URL, or platform-specific identifier)
 * @param disabled - Whether the link is disabled
 * @param variant - Visual style variant
 * @param className - Additional CSS classes (web only)
 *
 * @example
 * // Basic navigation link
 * <ButtonLink to="/admin/providers" variant="outline">
 *   View Providers
 * </ButtonLink>
 *
 * @example
 * // Disabled state
 * <ButtonLink to="/profile" variant="ghost" disabled={!isAuthenticated}>
 *   Profile
 * </ButtonLink>
 *
 * @example
 * // With custom styling
 * <ButtonLink to="/settings" variant="ghost-sm" className="ml-auto">
 *   Settings
 * </ButtonLink>
 */
export const ButtonLink: React.FC<{
  children: React.ReactNode;
  to: string;
  disabled?: boolean;
  variant?: "primary" | "ghost" | "compact" | "ghost-sm" | "overlay" | "outline";
  className?: string;
}> = ({ children, to, disabled, variant, className }) => {
  if (!impl) {
    // Fallback for development - shouldn't happen in production
    return <span style={{ display: "inline-block", opacity: 0.5 }}>{children}</span>;
  }
  return impl({ children, href: to, disabled, variant, className });
};
